import { storyblokEditable } from "@storyblok/react";
import Image from "next/image";
import { sbBlok } from "@/types/sb-types";
import Cta from "./Cta";
import {
  NODE_PARAGRAPH,
  NODE_UL,
  NODE_OL,
  NODE_LI,
  NODE_HEADING,
  render,
} from "storyblok-rich-text-react-renderer";
import clsx from "clsx";

interface HeroTextProps {
  blok: sbBlok;
}

const HeroText = ({ blok }: HeroTextProps) => {
  const {
    cta_link,
    cta_link_name,
    content_text,
    image,
    image_mobile,
    mobile_alignment,
  } = blok;

  const hasMobileImage = !!image_mobile?.filename;

  return (
    <section {...storyblokEditable(blok)}>
      <div className="grid-container m-0 max-w-none">
        <div className="col-start-1 col-span-4 row-start-1 lg:col-start-1 lg:col-span-16 2xl:col-start-1 2xl:col-span-32">
          {hasMobileImage && (
            <div className="block md:hidden relative w-full h-150 aspect-square">
              <Image
                src={image_mobile.filename}
                alt={image_mobile.alt || ""}
                className="object-cover w-full h-full"
                priority
                sizes="(max-width: 809px) 100vw, 1px"
                fill
              />
            </div>
          )}

          {image?.filename && (
            <div
              className={clsx("relative w-full h-150 aspect-square", {
                "hidden md:block": hasMobileImage,
              })}
            >
              <Image
                src={image.filename}
                alt={image.alt || ""}
                className="object-cover w-full h-full"
                fill
                priority
                sizes={hasMobileImage ? "(min-width: 810px) 100vw, 1px" : "100vw"}
              />
            </div>
          )}
        </div>
        <div
          className={clsx(
            "col-start-1 col-span-4 row-start-1 lg:col-start-5 lg:col-span-8 2xl:col-start-10 2xl:col-span-14 flex md:items-center",
            {
              "items-center": mobile_alignment === "center",
              "items-end pb-20 md:pb-0": mobile_alignment === "bottom",
            },
          )}
        >
          <div className="z-1 mx-5 w-full text-center md:m-auto md:max-w-150 xl:max-w-200">
            {content_text &&
              render(content_text, {
                nodeResolvers: {
                  [NODE_PARAGRAPH]: (children) => (
                    <p className="text-base xl:text-xl my-2.5 md:my-5">
                      {children}
                    </p>
                  ),
                  [NODE_UL]: (children) => (
                    <ul className="text-left lg:text-center pl-10 list-disc">
                      {children}
                    </ul>
                  ),
                  [NODE_OL]: (children) => (
                    <ol className="text-left lg:text-center pl-10 list-decimal">
                      {children}
                    </ol>
                  ),
                  [NODE_LI]: (children) => (
                    <li className="mb-4 last:mb-0">{children}</li>
                  ),
                  [NODE_HEADING]: (
                    children,
                    props: { level: 1 | 2 | 3 | 4 | 5 | 6 },
                  ) => {
                    if (props?.level === 1) return <h1>{children}</h1>;

                    if (props?.level === 2) return <h2>{children}</h2>;

                    return (
                      <h3 className="font-normal text-2xl lg:text-3xl xl:text-[40px]">
                        {children}
                      </h3>
                    );
                  },
                },
              })}
            {(cta_link?.url?.length > 0 ||
              cta_link?.story?.slug?.length > 0) && (
              <Cta
                href={cta_link?.url}
                fullSlug={cta_link?.story?.full_slug}
                name={cta_link_name}
                className="mx-auto"
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroText;
